import { apiClient } from '@/api/client'
import type { ApiResponse, ItemEntity, Warehouse } from '@/api/types'
import { endpoints } from '@/api/endpoints'
import { warehouseService } from '@/services/warehouseService'

function unwrapData<T>(payload: any): T {
  if (payload && typeof payload === 'object') {
    if ('success' in payload && 'data' in payload) return payload.data as T
    if ('data' in payload) return payload.data as T
  }
  return payload as T
}

function mapWarehouse(item: any): Warehouse {
  return {
    id: Number(item?.id ?? item?.warehouseId ?? 0),
    code: String(item?.code ?? item?.warehouseCode ?? ''),
    name: String(item?.name ?? item?.warehouseName ?? ''),
    location: String(item?.location ?? ''),
    isTemperatureControlled: Boolean(item?.isTemperatureControlled ?? false),
    email: String(item?.email ?? ''),
    empId: Number(item?.empId ?? item?.headEmpId ?? 0),
    wareHouseManagerName: String(item?.wareHouseManagerName ?? item?.managerName ?? ''),
    contactNumber: String(item?.contactNumber ?? item?.contact ?? ''),
    operationalHours: String(item?.operationalHours ?? ''),
    isWarehouseDistribution: Boolean(item?.isWarehouseDistribution ?? false),
    branchId: Number(item?.branchId ?? 0),
    city: Number(item?.city ?? 0),
    country: Number(item?.country ?? 0),
  }
}

function mapItem(item: any): ItemEntity {
  return {
    itemId: Number(item?.itemId ?? item?.id ?? 0),
    itemNameEn: String(item?.itemNameEn ?? ''),
    itemNameAr: String(item?.itemNameAr ?? ''),
    itemCode: String(item?.itemCode ?? ''),
    reference: String(item?.reference ?? ''),
    itemCategoryId: Number(item?.itemCategoryId ?? 0),
    sku: String(item?.sku ?? ''),
    barcode: String(item?.barcode ?? ''),
    tags: String(item?.tags ?? ''),
    shelfId: String(item?.shelfId ?? ''),
    description: String(item?.description ?? ''),
    subCategoryId: Number(item?.subCategoryId ?? 0),
    uom: Number(item?.uom ?? 0),
    type: Number(item?.type ?? 0),
    costMethod: Number(item?.costMethod ?? 0),
    isTaxable: Boolean(item?.isTaxable ?? false),
    unitSellingPrice: Number(item?.unitSellingPrice ?? 0),
    unitCostPrice: Number(item?.unitCostPrice ?? 0),
    tax: Number(item?.tax ?? 0),
    sellingTax: Number(item?.sellingTax ?? 0),
    attachmentUrl: String(item?.attachment ?? item?.attachmentUrl ?? ''),
    createdDate: item?.createdDate ?? undefined,
    createdBy: item?.createdBy ?? undefined,
    modifiedDate: item?.modifiedDate ?? undefined,
    modifiedBy: item?.modifiedBy ?? undefined,
    branchId: Number(item?.branchId ?? 0),
  }
}

export class InventoryMovementService {
  private buildInventoryTransferLinesJson(
    lines: Array<{
      id: number
      transferId: number
      itemId: number
      batchId: number
      fromWarehouseId: number
      toWarehouseId: number
      previousQuantity: number
      quantityTransfered: number
      newQuantity: number
      isDeleted: string
    }>,
  ): string {
    // Swagger example me key visual order fix karne ke liye mapping.
    const normalized = lines.map((l) => ({
      previousQuantity: l.previousQuantity,
      toWarehouseId: l.toWarehouseId,
      transferId: l.transferId,
      newQuantity: l.newQuantity,
      itemId: l.itemId,
      isDeleted: l.isDeleted,
      fromWarehouseId: l.fromWarehouseId,
      quantityTransfered: l.quantityTransfered,
      batchId: l.batchId,
      id: l.id,
    }))
    return JSON.stringify(normalized)
  }

  private appendFormField(formData: FormData, key: string, value: unknown) {
    if (value === undefined || value === null) {
      formData.append(key, '')
      return
    }
    formData.append(key, String(value))
  }

  private buildSaveUpdateFormData(payload: {
    id: number
    code: string
    sourceWarehouseId: number
    destinationWarehouseId: number
    movementDate: string
    movementType: number
    // inventory movement Swagger only needs Remarks + BranchId
    remarks: string
    branchId: number
    attachmentFile?: File | null
    itemList: Array<{
      id: number
      transferId: number
      itemId: number
      batchId: number
      fromWarehouseId: number
      toWarehouseId: number
      previousQuantity: number
      quantityTransfered: number
      newQuantity: number
      isDeleted: string
    }>
  }): FormData {
    const formData = new FormData()
    const resolvedCode =
      String(payload.code ?? '').trim() ||
      String(payload.movementDate ?? '').trim() ||
      String(payload.remarks ?? '').trim() ||
      (payload.branchId > 0 ? `IM-B${payload.branchId}-${Date.now()}` : `IM-${Date.now()}`)

    // Swagger order aligned.
    this.appendFormField(formData, 'Id', payload.id)
    this.appendFormField(formData, 'Code', resolvedCode)
    this.appendFormField(formData, 'SourceWarehouseId', payload.sourceWarehouseId)
    this.appendFormField(formData, 'DestinationWarehouseId', payload.destinationWarehouseId)
    this.appendFormField(formData, 'MovementDate', payload.movementDate)
    this.appendFormField(formData, 'MovementType', payload.movementType)
    this.appendFormField(formData, 'Remarks', payload.remarks)
    this.appendFormField(formData, 'AttachmentFilePath', '')

    if (payload.attachmentFile) {
      formData.append('AttachmentFile', payload.attachmentFile)
    } else {
      this.appendFormField(formData, 'AttachmentFile', '')
    }

    this.appendFormField(formData, 'BranchId', payload.branchId)

    const inventoryTransferLinesJson = this.buildInventoryTransferLinesJson(payload.itemList)
    // OpenAPI: itemList is array<object> in multipart/form-data
    payload.itemList.forEach((line, index) => {
      this.appendFormField(formData, `itemList[${index}].id`, line.id)
      this.appendFormField(formData, `itemList[${index}].transferId`, line.transferId)
      this.appendFormField(formData, `itemList[${index}].itemId`, line.itemId)
      this.appendFormField(formData, `itemList[${index}].batchId`, line.batchId)
      this.appendFormField(formData, `itemList[${index}].fromWarehouseId`, line.fromWarehouseId)
      this.appendFormField(formData, `itemList[${index}].toWarehouseId`, line.toWarehouseId)
      this.appendFormField(formData, `itemList[${index}].previousQuantity`, line.previousQuantity)
      this.appendFormField(formData, `itemList[${index}].quantityTransfered`, line.quantityTransfered)
      this.appendFormField(formData, `itemList[${index}].newQuantity`, line.newQuantity)
      this.appendFormField(formData, `itemList[${index}].isDeleted`, line.isDeleted)
    })

    // OpenAPI: inventoryTransferLinesJson is a string field
    formData.append('inventoryTransferLinesJson', inventoryTransferLinesJson)
    return formData
  }

  async getById(id: number): Promise<{
    id: number
    code: string
    sourceBranchId: number
    sourceWarehouseId: number
    destinationBranchId: number
    destinationWarehouseId: number
    movementDate: string
    movementType: number
    remarks: string
    attachmentFilePath: string
  }> {
    // GET endpoints backend likely uses Branch routes; leaving same as existing implementation.
    const response = await apiClient.get<ApiResponse<any> | any>(endpoints.inventoryMovement.getById(id))
    const payload = unwrapData<any>(response.data)
    return {
      id: Number(payload?.id ?? payload?.Id ?? 0),
      code: String(payload?.code ?? payload?.Code ?? ''),
      sourceBranchId: Number(payload?.sourceBranchId ?? payload?.SourceBranchId ?? 0),
      sourceWarehouseId: Number(payload?.sourceWarehouseId ?? payload?.SourceWarehouseId ?? 0),
      destinationBranchId: Number(payload?.destinationBranchId ?? payload?.DestinationBranchId ?? 0),
      destinationWarehouseId: Number(payload?.destinationWarehouseId ?? payload?.DestinationWarehouseId ?? 0),
      movementDate: String(payload?.movementDate ?? payload?.MovementDate ?? ''),
      movementType: Number(payload?.movementType ?? payload?.MovementType ?? 0),
      remarks: String(payload?.remarks ?? payload?.Remarks ?? ''),
      attachmentFilePath: String(
        payload?.attachmentFilePath ??
          payload?.AttachmentFilePath ??
          payload?.attachmentPath ??
          payload?.AttachmentPath ??
          payload?.attachmentUrl ??
          payload?.AttachmentUrl ??
          '',
      ),
    }
  }

  async getLinesByTransferId(id: number): Promise<
    Array<{
      id: number
      itemId: number
      batchId: number
      previousQuantity: number
      quantityTransfered: number
    }>
  > {
    const response = await apiClient.get<ApiResponse<any> | any>(endpoints.inventoryMovement.getLinesByTransferId(id))
    const payload = unwrapData<any>(response.data)
    const arr = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : []
    return arr.map((x: any) => ({
      id: Number(x?.id ?? x?.Id ?? 0),
      itemId: Number(x?.itemId ?? x?.ItemId ?? 0),
      batchId: Number(x?.batchId ?? x?.BatchId ?? 0),
      previousQuantity: Number(x?.previousQuantity ?? x?.PreviousQuantity ?? 0),
      quantityTransfered: Number(x?.quantityTransfered ?? x?.QuantityTransfered ?? 0),
    }))
  }

  async getAllInventoryTransfers(
    pageNumber: number,
    pageSize: number,
  ): Promise<{
    items: Array<{
      id: string
      code: string
      movementDate?: string
      remarks?: string
      sourceWarehouse: string
      destinationWarehouse: string
      attachmentFilePath?: string
    }>
    totalCount: number
  }> {
    const response = await apiClient.get<ApiResponse<any> | any>(
      endpoints.inventoryMovement.getAllInventoryTransfers(pageSize, pageNumber),
    )
    const payload = unwrapData<any>(response.data)
    const arr = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : []
    const totalCount = Number(payload?.totalRecords ?? payload?.totalCount ?? arr.length)

    const uniqueWarehouseIds: number[] = Array.from(
      new Set<number>(
        arr.flatMap((x: any) => [
          Number(x?.sourceWarehouseId || x?.fromWarehouseId || 0),
          Number(x?.destinationWarehouseId || x?.toWarehouseId || 0),
        ]),
      ),
    ).filter((id) => id > 0)

    const warehouseNameById: Record<number, string> = {}
    await Promise.all(
      uniqueWarehouseIds.map(async (warehouseId) => {
        try {
          const wh = await warehouseService.getWarehouseById(warehouseId)
          warehouseNameById[warehouseId] = wh.name || wh.code || String(warehouseId)
        } catch {
          warehouseNameById[warehouseId] = String(warehouseId)
        }
      }),
    )

    const items = arr.map((x: any) => ({
      id: String(x?.id ?? x?.transferId ?? ''),
      code: String(x?.code ?? ''),
      movementDate: String(x?.movementDate ?? x?.MovementDate ?? ''),
      remarks: String(x?.remarks ?? x?.Remarks ?? ''),
      sourceWarehouse: String(
        x?.sourceWarehouseName ??
          x?.fromWarehouseName ??
          x?.sourceWarehouse ??
          warehouseNameById[Number(x?.sourceWarehouseId || x?.fromWarehouseId || 0)] ??
          x?.sourceWarehouseId ??
          x?.fromWarehouseId ??
          '',
      ),
      destinationWarehouse: String(
        x?.destinationWarehouseName ??
          x?.toWarehouseName ??
          x?.destinationWarehouse ??
          warehouseNameById[Number(x?.destinationWarehouseId || x?.toWarehouseId || 0)] ??
          x?.destinationWarehouseId ??
          x?.toWarehouseId ??
          '',
      ),
      attachmentFilePath: String(
        x?.attachmentFilePath ??
          x?.AttachmentFilePath ??
          x?.attachmentPath ??
          x?.AttachmentPath ??
          x?.attachmentUrl ??
          x?.AttachmentUrl ??
          x?.documentAttachUrl ??
          x?.DocumentAttachUrl ??
          '',
      ),
    }))

    return { items, totalCount: Number.isFinite(totalCount) ? totalCount : items.length }
  }

  async getWarehousesByBranchId(branchId: number): Promise<Warehouse[]> {
    const url = endpoints.inventoryMovement.getWarehousesByBranchId(branchId)
    console.log('[InventoryMovement] getWarehousesByBranchId URL:', url)
    const response = await apiClient.get<ApiResponse<any> | any>(url)
    const payload = unwrapData<any>(response.data)
    const arr = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : []
    return arr.map(mapWarehouse)
  }

  /** Warehouses for Inventory Dispatch — uses `/api/InventoryTransferBranch/GetWarehousesByBranchId` */
  async getWarehousesByBranchIdTransferBranch(branchId: number): Promise<Warehouse[]> {
    const url = endpoints.inventoryTransferBranch.getWarehousesByBranchId(branchId)
    const response = await apiClient.get<ApiResponse<any> | any>(url)
    const payload = unwrapData<any>(response.data)
    const arr = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : []
    return arr.map(mapWarehouse)
  }

  /** Items in source warehouse — `/api/InventoryTransferBranch/GetItemsByWarehouse/{whId}` */
  async getItemsByWarehouseTransferBranch(whId: number): Promise<ItemEntity[]> {
    const response = await apiClient.get<ApiResponse<any> | any>(endpoints.inventoryTransferBranch.getItemsByWarehouse(whId))
    const payload = unwrapData<any>(response.data)
    const arr = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : []
    return arr.map(mapItem)
  }

  /** Batches for item at source warehouse — `/api/InventoryTransferBranch/GetBatchesByWarehouseAndItem` */
  async getBatchesByWarehouseAndItemTransferBranch(
    whId: number,
    itemId: number,
  ): Promise<Array<{ id: number; code: string; reference: string; availableQuantity: number }>> {
    const response = await apiClient.get<ApiResponse<any> | any>(
      endpoints.inventoryTransferBranch.getBatchesByWarehouseAndItem(whId, itemId),
    )
    const payload = unwrapData<any>(response.data)
    const arr = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : []
    return arr.map((x: any) => ({
      id: Number(x?.id ?? 0),
      code: String(x?.code ?? ''),
      reference: String(x?.reference ?? ''),
      availableQuantity: Number(x?.availableQuantity ?? 0),
    }))
  }

  /** Stock at source warehouse for item+batch — `/api/InventoryTransferBranch/GetItemDetails` */
  async getItemDetailsTransferBranch(whId: number, itemId: number, bId: number): Promise<{ availableQuantity: number }> {
    const response = await apiClient.get<ApiResponse<any> | any>(
      endpoints.inventoryTransferBranch.getItemDetails(whId, itemId, bId),
    )
    const payload = unwrapData<any>(response.data)
    return { availableQuantity: Number(payload?.availableQuantity ?? payload?.AvailableQuantity ?? 0) }
  }

  async getItemsByWarehouse(whId: number): Promise<ItemEntity[]> {
    const response = await apiClient.get<ApiResponse<any> | any>(endpoints.inventoryMovement.getItemsByWarehouse(whId))
    const payload = unwrapData<any>(response.data)
    const arr = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : []
    return arr.map(mapItem)
  }

  async getBatchesByWarehouseAndItem(
    whId: number,
    itemId: number,
  ): Promise<Array<{ id: number; code: string; reference: string; availableQuantity: number }>> {
    const response = await apiClient.get<ApiResponse<any> | any>(
      endpoints.inventoryMovement.getBatchesByWarehouseAndItem(whId, itemId),
    )
    const payload = unwrapData<any>(response.data)
    const arr = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : []
    return arr.map((x: any) => ({
      id: Number(x?.id ?? 0),
      code: String(x?.code ?? ''),
      reference: String(x?.reference ?? ''),
      availableQuantity: Number(x?.availableQuantity ?? 0),
    }))
  }

  async getItemDetails(whId: number, itemId: number, bId: number): Promise<{ availableQuantity: number }> {
    const response = await apiClient.get<ApiResponse<any> | any>(
      endpoints.inventoryMovement.getItemDetails(whId, itemId, bId),
    )
    const payload = unwrapData<any>(response.data)
    const raw =
      payload?.availableQuantity ??
      payload?.AvailableQuantity ??
      payload?.balanceQuantity ??
      payload?.BalanceQuantity ??
      payload?.quantity ??
      payload?.Quantity ??
      0
    const n = Number(raw)
    return { availableQuantity: Number.isFinite(n) ? n : 0 }
  }

  async save(payload: {
    id: number
    code: string
    sourceWarehouseId: number
    destinationWarehouseId: number
    movementDate: string
    movementType: number
    remarks: string
    branchId: number
    attachmentFile?: File | null
    itemList: any[]
  }): Promise<void> {
    const inventoryTransferLinesJson = this.buildInventoryTransferLinesJson(payload.itemList as any)
    console.log('[InventoryMovement] save — master fields', {
      id: payload.id,
      code: payload.code,
      sourceWarehouseId: payload.sourceWarehouseId,
      destinationWarehouseId: payload.destinationWarehouseId,
      movementDate: payload.movementDate,
      movementType: payload.movementType,
      remarks: payload.remarks,
      branchId: payload.branchId,
    })
    console.log('[InventoryMovement] save — attachmentFile', payload.attachmentFile || null)
    console.log('[InventoryMovement] save — itemList parsed', payload.itemList)
    console.log('[InventoryMovement] save — inventoryTransferLinesJson', inventoryTransferLinesJson)

    const formData = this.buildSaveUpdateFormData(payload as any)
    await apiClient.post(endpoints.inventoryMovement.save, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  }

  async update(payload: {
    id: number
    code: string
    sourceWarehouseId: number
    destinationWarehouseId: number
    movementDate: string
    movementType: number
    remarks: string
    branchId: number
    attachmentFile?: File | null
    itemList: any[]
  }): Promise<void> {
    const inventoryTransferLinesJson = this.buildInventoryTransferLinesJson(payload.itemList as any)
    console.log('[InventoryMovement] update — master fields', {
      id: payload.id,
      code: payload.code,
      sourceWarehouseId: payload.sourceWarehouseId,
      destinationWarehouseId: payload.destinationWarehouseId,
      movementDate: payload.movementDate,
      movementType: payload.movementType,
      remarks: payload.remarks,
      branchId: payload.branchId,
    })
    console.log('[InventoryMovement] update — attachmentFile', payload.attachmentFile || null)
    console.log('[InventoryMovement] update — itemList parsed', payload.itemList)
    console.log('[InventoryMovement] update — inventoryTransferLinesJson', inventoryTransferLinesJson)

    const formData = this.buildSaveUpdateFormData(payload as any)
    await apiClient.post(endpoints.inventoryMovement.update, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  }
}

export const inventoryMovementService = new InventoryMovementService()

